/**
 * Het blok onder `[uit de tabel]` uit een antwoord halen en ontleden. R40-1.
 *
 * **DE MELDING VAN FRANK, 23 AUGUSTUS 2026:** "het blok onder [uit de tabel]
 * komt als een doorlopende regel in beeld, niet als tabel."
 *
 * **DE OORZAAK, GEMETEN EN NIET GERADEN.** Het antwoord dat hij op zijn scherm
 * had, draagt een keurige markdown-tabel: drie regels, zes cellen per regel, en
 * de rijregel is 883 tekens lang. `MessageBubble` rendert met `react-markdown`
 * ZONDER `remark-gfm`, en gewone CommonMark kent geen tabellen. Die drie regels
 * worden dus een alinea, en een alinea van 883 tekens is precies de doorlopende
 * regel die hij beschrijft.
 *
 * **WAAROM WIJ HET ZELF ONTLEDEN EN GEEN `remark-gfm` TOEVOEGEN.** Twee redenen,
 * en de tweede weegt zwaarder:
 *
 *   1. `remark-gfm` is een nieuwe afhankelijkheid met drie nieuwe pakketten
 *      eronder, en die vraagt een eigen besluit (werkregel 2.2).
 *   2. Het zou de opmaak van ELK bericht veranderen, ook alle bestaande. Dat is
 *      een wijziging aan werkende weergave waar niemand om heeft gevraagd. Dit
 *      blok is van ons, komt uit onze eigen code, en heeft een eigen merkteken;
 *      dan hoort de reparatie ook daar te zitten en nergens anders.
 *
 * **HET IS EEN PURE FUNCTIE, en dat is met opzet.** De beslissing zit buiten de
 * component, dus zij is op UITKOMST te toetsen in plaats van op de vraag of er
 * toevallig een `<table>` in de DOM staat.
 */

// Moet gelijk blijven aan `MELDING_KOP` in backend/services/table_lookup.py.
// Er is een wachter die dat afdwingt; zie tabelblok.test.js.
export const MERK = "\n\n---\n[uit de tabel]";

// Moet gelijk blijven aan MAX_CEL_TEKENS en KAPMERK in dezelfde module.
export const MAX_CEL_TEKENS = 300;
export const KAPMERK = " [...]";

/**
 * Kapt een cel, MERKTEKEN MEEGEREKEND, net als de backend.
 *
 * DIT STAAT HIER OOK, EN DAT IS GEEN DUBBELING UIT SLORDIGHEID. De backend kapt
 * bij het SCHRIJVEN; berichten die voor die reparatie zijn opgeslagen dragen nog
 * cellen van 306 tekens of langer. Franks eis gaat over wat er OP HET SCHERM
 * staat, en dat is de enige plek waar zij voor alle berichten waar te maken is.
 */
export function kap(cel) {
  const tekst = (cel || "").trim();
  if (tekst.length <= MAX_CEL_TEKENS) return tekst;
  return tekst.slice(0, MAX_CEL_TEKENS - KAPMERK.length).trimEnd() + KAPMERK;
}

function isScheiding(regel) {
  // `| --- | --- |`: alleen streepjes, dubbele punten en pijpen.
  return /^\s*\|[\s:|-]+\|\s*$/.test(regel);
}

function cellen(regel) {
  return regel.trim().replace(/^\|/, "").replace(/\|$/, "").split("|")
    .map((c) => c.trim());
}

/** Ontleedt het blok NA het merkteken. Geeft null als het er niet uitziet
 *  zoals wij het schrijven; dan valt de aanroeper terug op platte tekst. */
export function ontleed(blok) {
  const regels = (blok || "").split("\n");
  const bron = (regels[0] || "").trim().replace(/^\((.*)\)$/, "$1") || null;
  const tabelregels = regels.slice(1).filter((r) => r.trim().startsWith("|"));
  if (tabelregels.length < 2) return null;
  const kop = cellen(tabelregels[0]).map(kap);
  const rest = isScheiding(tabelregels[1]) ? tabelregels.slice(2) : tabelregels.slice(1);
  const rijen = rest.map((r) => cellen(r).map(kap));
  if (!rijen.length) return null;
  // De breedte van de tabel is de breedste regel, want de bron kan rafelig zijn:
  // gemeten draagt de WERKLOG-rij zes cellen onder een kop van vier.
  const breedte = Math.max(kop.length, ...rijen.map((r) => r.length));
  const vul = (r) => r.concat(Array(Math.max(0, breedte - r.length)).fill(""));
  return { bron, kop: vul(kop), rijen: rijen.map(vul) };
}

/**
 * Splitst een bericht in het gewone antwoord en het tabelblok.
 * Geen merkteken of onleesbaar blok: alles blijft tekst, precies zoals het was.
 */
export function splits(content) {
  const heel = content || "";
  const i = heel.indexOf(MERK);
  if (i === -1) return { tekst: heel, tabel: null };
  const tabel = ontleed(heel.slice(i + MERK.length));
  if (!tabel) return { tekst: heel, tabel: null };
  return { tekst: heel.slice(0, i), tabel };
}
